import { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import "../../Styles/style.css";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import IconButton from "@mui/material/IconButton";
import {
  Alert,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import CenterRegistrationApi from "../../components/centerRegistration/Api/api";
import { IndianStates } from "../../assets/IndianStates";
import { useSelector } from "react-redux";
import { Card } from "@mui/material";
import { canAddCenter, canUpdateCenter } from "../../utility/permissionUtils";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const stylesCss = {
  inputBox: {
    width: "100%",
    marginBottom: "10px",
  },
  gridStyle: {
    width: "80%",
    margin: "auto",
  },
  paddingInnerGrids: {
    paddingRight: "10px",
    paddingLeft: "10px",
    paddingTop: "10px",
  },
};

const CenterRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const reduxState = useSelector((state) => state.userReducer);

  const [formData, setFormData] = useState({
    primary_services: [],
    selectedServiceName: "",
    center_name: "",
    personal_details: {
      phone: {
        country_code: "+91",
        mobile_number: "",
      },
      email: "",
      name: "",
      user_name: "",
      about: "",
    },
    remark: "",
    remarkRequired: false,
    ticket_remarks: "",
    services: {
      primary_services: [
        {
          _id: "",
          service_name: "",
        },
      ],
      pincodes: "",
      secondary_services: [],
    },
    checkedSecondaryServices: [],
    address_details: {
      address_line: "",
      city: "",
      state: "",
      pincode: "",
      country: "INDIA",
    },
    no_of_technicians: "",
    automate_qr_code: false,
    qr_code: "",
    payment_details: {
      upi_id: "",
      paid_for_onboarding_kit: false,
      subscribe_start_date: null,
      subscribe_end_date: null,
    },
    toast: {
      open: false,
      msg: "",
      duration: 5000,
      type: "",
    },
    isFormValid: false,
    servicesList: [],
    sendingOtp: false,
    isOtpSentSuccess: false,
    otpEntered: "",
    isVerifyingOtp: false,
    isOtpVerfied: false,
    isRegistering: false,
    allClientsList: [],
    clients_ids_list: [],
    login_into_application: false,
    accepting_broadcast_ticket: false,
    doesCenterExists: false,
    isEditDetailsExists: false,
  });

  // Permission checks
  const userPermissions = reduxState?.adminDetails?.role_id?.permissions;
  const hasAddCenterPermission = canAddCenter(userPermissions);
  const hasUpdateCenterPermission = canUpdateCenter(userPermissions);

  const editCenterId = new URLSearchParams(location.search).get("edit");
  const isEditMode = editCenterId != null;

  // Check if user has permission to access this page
  const hasAccess = isEditMode
    ? hasUpdateCenterPermission
    : hasAddCenterPermission;

  useEffect(() => {
    if (!hasAccess) {
      return;
    }

    getAllServicesExists();
    getAllClients();
    fetchServicesList();

    if (isEditMode) {
      getCenterDetails(editCenterId);
    }
  }, [hasAccess, isEditMode]);

  const fetchServicesList = () => {
    if (!formData.primary_services) {
      const selectedService = formData.servicesList.find(
        (service) => service._id === formData.primary_services
      );
      if (selectedService) {
        setFormData((prev) => ({
          ...prev,
          primary_services: selectedService._id,
          selectedServiceName: selectedService.service_name,
        }));
      }
    }
  };

  const getCenterIdForEdit = () => {
    return new URLSearchParams(location.search).get("edit");
  };

  const getCenterDetails = (id) => {
    const payload = {
      center_obj_id: id,
    };

    setFormData((prev) => ({ ...prev, isEditDetailsExists: true }));

    new CenterRegistrationApi()
      .getCenterDetails(payload)
      .then((res) => {
        if (res?.data?.length) {
          setFormData((prev) => ({ ...prev, doesCenterExists: true }));
          updateStateOfEditData(res?.data?.[0]);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const updateStateOfEditData = (centerDetails) => {
    const toValidDate = (value) => {
      if (!value) return null;

      // Already a Date
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }

      // Dayjs / Moment adapters typically provide one of these
      if (typeof value === "object") {
        if (typeof value.toDate === "function") {
          const d = value.toDate();
          return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
        }

        // Dayjs internal Date
        if (value.$d instanceof Date) {
          return Number.isNaN(value.$d.getTime()) ? null : value.$d;
        }

        if (typeof value.valueOf === "function") {
          const d = new Date(value.valueOf());
          return Number.isNaN(d.getTime()) ? null : d;
        }
      }

      // String/number timestamps
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    // Format dates for DatePicker
    const subscribeStartDate = toValidDate(
      centerDetails?.payment_details?.subscribe_start_date
    );

    const subscribeEndDate = toValidDate(
      centerDetails?.payment_details?.subscribe_end_date
    );

    setFormData((prev) => ({
      ...prev,
      center_name: centerDetails?.center_name,
      personal_details: {
        phone: {
          country_code: "+91",
          mobile_number: centerDetails?.personal_details?.phone?.mobile_number,
        },
        email: centerDetails?.personal_details?.email || "",
        name: centerDetails?.personal_details?.name,
        user_name: centerDetails?.personal_details?.user_name,
        about: centerDetails?.personal_details?.about,
      },
      primary_services: centerDetails?.services?.primary_services?.[0]?._id,
      pincodes: centerDetails.services.pincodes.join(","),
      secondary_services: centerDetails?.services?.secondary_services?.map(
        (service) => service?.secondary_services_id?._id
      ),
      address_details: {
        address_line: centerDetails?.address_details?.address_line,
        city: centerDetails?.address_details?.city,
        state: centerDetails?.address_details?.state,
        pincode: centerDetails?.address_details?.pincode,
        country: "INDIA",
      },
      payment_details: {
        upi_id: centerDetails?.payment_details?.upi_id || "",
        paid_for_onboarding_kit:
          centerDetails?.payment_details?.paid_for_onboarding_kit || false,
        subscribe_start_date: subscribeStartDate,
        subscribe_end_date: subscribeEndDate,
      },
      qr_id: centerDetails?.qr_details?.qr_id,
      no_of_technicians: centerDetails?.no_of_technicians,
      clients_ids_list: centerDetails?.clients_ids_list?.map(
        (item) => item._id
      ),
      login_into_application:
        centerDetails?.disabled_for?.login_into_application,
      accepting_broadcast_ticket:
        centerDetails?.disabled_for?.accepting_broadcast_ticket,
      checkedSecondaryServices:
        centerDetails?.services?.secondary_services?.map(
          (service) => service?.secondary_services_id?._id
        ),
    }));
  };

  const getAllClients = () => {
    new CenterRegistrationApi()
      .getAllClients()
      .then((res) => {
        setFormData((prev) => ({ ...prev, allClientsList: res.data }));
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const getAllServicesExists = () => {
    new CenterRegistrationApi()
      .getAllActiveServices()
      .then((res) => {
        setFormData((prev) => ({ ...prev, servicesList: res.data }));
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const showDialog = (open, type, msg) => {
    setFormData((prev) => ({
      ...prev,
      toast: {
        open: open,
        msg: msg,
        duration: 5000,
        type: type,
      },
    }));
  };

  const closeDialog = (open = false) => {
    setFormData((prev) => ({
      ...prev,
      toast: {
        open: open,
        msg: "",
        duration: 5000,
        type: "",
      },
    }));
  };

  const handleInputChangeClients = (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      clients_ids_list: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleWhatsAppClick = (name, phoneNumber) => {
    const formattedPhoneNumber = phoneNumber.replace(/[^\d]/g, "");
    const message = `
    नमस्ते ${name}
    It's pleasure to hv you on OniT. 
We wanted to inform you that your free trial period is over. 
I understand you hv not yet completed KYC/ shared QR to get FREE leads/ not completed 15 request of your customers to enable you get new customers from our portal. 
We have extended the FREE period further to give you more time to experience the benefits of our services.

Download the Onit Partners app through the link below. Share your QR code from profile
Create Request, Assign and Complete with pictures and price charged for service provided to customer  https://play.google.com/store/apps/details?id=com.onit.partners
   
    वॉलेट में मिनिमम 99/– होना चाहिए
`;

    const encodedMessage = encodeURIComponent(message);
    window.open(
      `https://web.whatsapp.com/send?phone=${formattedPhoneNumber}&text=${encodedMessage}`,
      "_blank"
    );
  };

  const handleChangePincodes = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, pincodes: value }));
  };

  const getFormattedServices = (services) => {
    let secondary_services_list = [];
    services &&
      services.length > 0 &&
      services.forEach((item) => {
        const serviceName = item?.secondary_services_id?.[0]?.service_name;
        if (serviceName) {
          const parts = serviceName.split("-");
          const serviceNameFirstPart = parts[0]?.trim();
          if (serviceNameFirstPart) {
            secondary_services_list.push(serviceNameFirstPart);
          }
        }
      });
    return secondary_services_list.join(", ");
  };

  const handleChangePersonalDetails = (e) => {
    const { name, value } = e.target;

    // Handle fields directly under personal_details
    if (name === "email" || name === "name" || name === "user_name" || name === "about") {
      setFormData((prev) => ({
        ...prev,
        personal_details: {
          ...prev.personal_details,
          [name]: value,
        },
      }));
    } else {
      // Handle phone fields
      setFormData((prev) => ({
        ...prev,
        personal_details: {
          ...prev.personal_details,
          phone: {
            ...prev.personal_details.phone,
            [name]: value,
          },
        },
      }));
    }
  };

  const handleResetStateValues = () => {
    setFormData({
      ...formData,
      center_name: "",
      personal_details: {
        phone: {
          country_code: "+91",
          mobile_number: "",
        },
        email: "",
        name: "",
        user_name: "",
        about: "",
      },
      primary_services: "",
      secondary_services: {
        secondary_services_id: "",
        priority: "",
      },
      address_details: {
        address_line: "",
        city: "",
        state: "",
        pincode: "",
        country: "INDIA",
      },
      no_of_technicians: "",
      qr_code: "",
      payment_details: {
        upi_id: "",
        paid_for_onboarding_kit: false,
        subscribe_start_date: null,
        subscribe_end_date: null,
      },
      checkedSecondaryServices: [],
      isFormValid: false,
      sendingOtp: false,
      isOtpSentSuccess: false,
      otpEntered: "",
      isVerifyingOtp: false,
      isOtpVerfied: false,
      isRegistering: false,
      clients_ids_list: [],
      remark: "",
      ticket_remarks: "",
    });
  };

  const handleChangeAddressDetails = (e) => {
    const { name, value } = e.target;

    if (name === "pincode") {
      if (value.length <= 6) {
        setFormData((prev) => ({
          ...prev,
          address_details: {
            ...prev.address_details,
            [name]: value,
          },
        }));
      }
      return;
    }

    setFormData((prev) => ({
      ...prev,
      address_details: {
        ...prev.address_details,
        [name]: value,
      },
    }));
  };

  const handleChangeSecondaryServiceDetails = (event) => {
    const { value } = event.target;
    setFormData((prev) => ({ ...prev, checkedSecondaryServices: value }));
  };

  const handleChangeInputOuter = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle subscription date changes
  const handleSubscriptionDateChange = (name, date) => {
    setFormData((prev) => ({
      ...prev,
      payment_details: {
        ...prev.payment_details,
        [name]: date,
      },
    }));
  };

  // Handle paid for onboarding kit change
  const handlePaidForOnboardingKitChange = (event) => {
    const isChecked = event.target.checked;

    setFormData((prev) => ({
      ...prev,
      payment_details: {
        ...prev.payment_details,
        paid_for_onboarding_kit: isChecked,
        // If turning off, clear the dates
        subscribe_start_date: isChecked
          ? prev.payment_details.subscribe_start_date
          : null,
        subscribe_end_date: isChecked
          ? prev.payment_details.subscribe_end_date
          : null,
      },
    }));
  };

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Format date to YYYY-MM-DD for API
  const formatDateForApi = (date) => {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().split("T")[0];
  };

  const handleRegister = () => {
    // Check permissions again before proceeding
    if (isEditMode && !hasUpdateCenterPermission) {
      showDialog(true, "error", "You don't have permission to update centers.");
      return;
    }

    if (!isEditMode && !hasAddCenterPermission) {
      showDialog(true, "error", "You don't have permission to add centers.");
      return;
    }

    // Validate email
    if (
      formData.personal_details.email &&
      !isValidEmail(formData.personal_details.email)
    ) {
      showDialog(true, "error", "Please enter a valid email address.");
      return;
    }

    const normalizeToValidDate = (value) => {
      if (!value) return null;
      const d = value instanceof Date ? value : new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    // Validate subscription dates when paid for onboarding kit is checked
    if (formData.payment_details.paid_for_onboarding_kit) {
      const startDate = normalizeToValidDate(
        formData.payment_details.subscribe_start_date
      );
      const endDate = normalizeToValidDate(formData.payment_details.subscribe_end_date);

      if (
        !startDate ||
        !endDate
      ) {
        showDialog(
          true,
          "error",
          "Please select both subscription start and end dates when 'Paid for Onboarding Kit' is enabled."
        );
        return;
      }

      // Validate that end date is after start date
      if (endDate.getTime() < startDate.getTime()) {
        showDialog(
          true,
          "error",
          "Subscription end date must be after start date."
        );
        return;
      }
    }

    const { checkedSecondaryServices, remark, remarkRequired } = formData;

    const filteredServices = checkedSecondaryServices.filter(
      (service) => service !== null
    );
    const pincodesArray = formData.pincodes
      .split(",")
      .map((pincode) => pincode.trim())
      .filter((pincode) => pincode.length > 0);

    if (remarkRequired && !remark) {
      showDialog(
        true,
        "error",
        "Remark is required when disabling login or accepting tickets."
      );
      return;
    }

    let payload = {
      center_name: formData.center_name,
      personal_details: formData.personal_details,
      primary_services: formData.primary_services,
      pincodes: pincodesArray,
      checkedSecondaryServices: formData.checkedSecondaryServices,
      address_details: formData.address_details,
      no_of_technicians: formData.no_of_technicians,
      clients_ids_list: formData.clients_ids_list,
    };

    setFormData((prev) => ({ ...prev, isRegistering: true }));

    if (formData.isEditDetailsExists) {
      payload.center_obj_id = getCenterIdForEdit();
      payload = {
        ...payload,
        login_into_application: formData.login_into_application,
        accepting_broadcast_ticket: formData.accepting_broadcast_ticket,
        upi_id: formData.payment_details.upi_id,
        remark: formData.remark,
        ticket_remarks: formData.ticket_remarks,
        update_name: reduxState?.adminDetails?.user_name,
        // Add subscription details to payload
        payment_details: {
          paid_for_onboarding_kit:
            formData.payment_details.paid_for_onboarding_kit,
          subscribe_start_date: formatDateForApi(
            formData.payment_details.subscribe_start_date
          ),
          subscribe_end_date: formatDateForApi(
            formData.payment_details.subscribe_end_date
          ),
        },
      };
      new CenterRegistrationApi()
        .updateCenterAdmin(payload)
        .then((res) => {
          showDialog(true, "success", "Center Updated ");
          setFormData((prev) => ({ ...prev, isRegistering: false }));
        })
        .catch((err) => {
          console.log(err);
          setFormData((prev) => ({ ...prev, isRegistering: false }));
          showDialog(true, "error", err?.response?.data?.message);
        });

      return;
    }

    payload = {
      ...payload,
      qr_code: formData.qr_code,
      automate_qr_code: formData.automate_qr_code,
    };

    new CenterRegistrationApi()
      .adminCreateCenter(payload)
      .then((res) => {
        showDialog(true, "success", "Center added ! Add more centers ");
        setFormData((prev) => ({ ...prev, isRegistering: false }));
        handleResetStateValues();
      })
      .catch((err) => {
        console.log(err);
        setFormData((prev) => ({ ...prev, isRegistering: false }));
        showDialog(true, "error", err?.response?.data?.message);
      });
  };

  const handleChangeCheckBox1 = (event, name) => {
    setFormData((prev) => ({ ...prev, [name]: event.target.checked }));
  };

  const handleChangeCheckBox = (event, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: event.target.checked,
      remarkRequired: true,
    }));
  };

  // Show access denied if user doesn't have permission
  if (!hasAccess) {
    return (
      <div className="max-w-8xl mx-auto px-4 pb-8 flex justify-center items-center min-h-96">
        <Card className="rounded-lg shadow-md p-8 text-center">
          <Typography variant="h5" color="error" className="mb-4">
            Access Denied
          </Typography>
          <Typography variant="body1">
            You don't have permission to {isEditMode ? "update" : "add"}{" "}
            centers. Please contact your administrator.
          </Typography>
        </Card>
      </div>
    );
  }

  if (formData.isEditDetailsExists && !formData.doesCenterExists) {
    return (
      <div className="max-w-8xl mx-auto px-4 pb-8 flex justify-center items-center min-h-96">
        <Typography variant="h6" color="error">
          Center not found!
        </Typography>
      </div>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="max-w-8xl mx-auto px-4 pb-8">
        {/* Toast Notification */}
        <Snackbar
          open={formData.toast.open}
          autoHideDuration={formData.toast.duration}
          onClose={closeDialog}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={closeDialog}
            severity={formData.toast.type === "error" ? "error" : "success"}
            sx={{ width: "100%" }}
          >
            {formData.toast.msg}
          </Alert>
        </Snackbar>

        {/* Main Form */}
        <Typography variant="h4" className="font-bold mb-6 text-center">
          {formData.isEditDetailsExists ? "Update Center" : "Add New Center"}
        </Typography>

        <Card className="rounded-lg shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Center Name */}
            <TextField
              fullWidth
              helperText="Please provide your Shop / Center (Name)"
              label="Shop / Center (Name)"
              name="center_name"
              onChange={handleChangeInputOuter}
              error={formData.isFormValid && !formData.center_name}
              value={formData.center_name}
            />

            {/* Contact Number */}
            <TextField
              fullWidth
              helperText="Please provide your contact number"
              label="Contact Number"
              name="mobile_number"
              disabled={formData.isOtpSentSuccess}
              onChange={handleChangePersonalDetails}
              error={
                formData.isFormValid &&
                !formData.personal_details.phone.mobile_number
              }
              value={formData.personal_details.phone.mobile_number}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography>+91</Typography>
                  </InputAdornment>
                ),
              }}
              inputProps={{ maxLength: 10 }}
            />

            {/* Name Field */}
            <TextField
              fullWidth
              helperText="Please provide your name"
              label="Name"
              name="name"
              onChange={handleChangePersonalDetails}
              error={formData.isFormValid && !formData.personal_details.name}
              value={formData.personal_details.name}
              inputProps={{ maxLength: 50 }}
            />

            {/* Primary Service */}
            <FormControl fullWidth>
              <InputLabel id="primary-service-label">
                Main Service You Offer / Need
              </InputLabel>
              <Select
                labelId="primary-service-label"
                id="primary-service-select"
                disabled={formData.isEditDetailsExists}
                label="Main Service You Offer / Need"
                name="primary_services"
                onChange={handleChangeInputOuter}
                error={formData.isFormValid && !formData.primary_services}
                value={formData.primary_services}
              >
                {formData.servicesList?.map((service) => (
                  <MenuItem key={service._id} value={service._id}>
                    <Checkbox
                      checked={formData.primary_services === service._id}
                    />
                    <ListItemText primary={service.service_name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Secondary Services */}
            <FormControl fullWidth>
              <InputLabel id="secondary-services-label">
                Other Services You Offer / Need
              </InputLabel>
              <Select
                labelId="secondary-services-label"
                id="secondary-services-select"
                label="Other Services You Offer / Need"
                name="secondary_services"
                onChange={handleChangeSecondaryServiceDetails}
                value={formData.checkedSecondaryServices}
                multiple
                renderValue={(selected) => {
                  const selectedServices = formData.servicesList.filter(
                    (service) => selected.includes(service._id)
                  );
                  return selectedServices
                    .map((service) => service.service_name)
                    .join(", ");
                }}
              >
                {formData.servicesList.map((service) => (
                  <MenuItem key={service._id} value={service._id}>
                    <Checkbox
                      checked={formData.checkedSecondaryServices.includes(
                        service._id
                      )}
                    />
                    <ListItemText primary={service.service_name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Clients */}
            <FormControl fullWidth>
              <InputLabel id="clients-label">Choose clients</InputLabel>
              <Select
                labelId="clients-label"
                id="clients-select"
                label="Choose clients"
                name="clients_ids_list"
                multiple
                onChange={handleInputChangeClients}
                error={
                  formData.isFormValid && !formData.clients_ids_list.length
                }
                value={formData.clients_ids_list}
              >
                {formData.allClientsList?.map((ite) => {
                  return <MenuItem value={ite._id}>{ite.client_name}</MenuItem>;
                })}
              </Select>
            </FormControl>

            {/* Address */}
            <TextField
              fullWidth
              helperText="Please provide your address"
              label="Address"
              name="address_line"
              onChange={handleChangeAddressDetails}
              error={
                formData.isFormValid && !formData?.address_details?.address_line
              }
              value={formData?.address_details?.address_line}
            />

            {/* City */}
            <TextField
              fullWidth
              helperText="Please enter your city"
              label="City"
              name="city"
              onChange={handleChangeAddressDetails}
              error={formData.isFormValid && !formData.address_details.city}
              value={formData?.address_details?.city}
              inputProps={{ maxLength: 30 }}
            />

            {/* State */}
            <FormControl fullWidth>
              <InputLabel id="state-label">State</InputLabel>
              <Select
                labelId="state-label"
                id="state-select"
                label="State"
                name="state"
                onChange={handleChangeAddressDetails}
                value={
                  formData?.address_details?.state == undefined
                    ? ""
                    : formData?.address_details?.state
                }
                error={formData.isFormValid && !formData.address_details.state}
              >
                {IndianStates?.map((ite) => {
                  return <MenuItem value={ite.value}>{ite.value}</MenuItem>;
                })}
              </Select>
            </FormControl>

            {/* Country */}
            <TextField
              fullWidth
              label="Country"
              name="country"
              onChange={handleChangeAddressDetails}
              error={formData.isFormValid && !formData.address_details.country}
              value={formData.address_details.country}
              disabled
              inputProps={{ maxLength: 30 }}
            />

            {/* Paid for Onboarding Kit */}
            {formData.isEditDetailsExists && (
              <FormControl fullWidth>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          formData.payment_details.paid_for_onboarding_kit
                        }
                        onChange={handlePaidForOnboardingKitChange}
                      />
                    }
                    label="Paid for Onboarding Kit"
                  />
                </FormGroup>
              </FormControl>
            )}

            {/* Subscription Start Date - Show only when Paid for Onboarding Kit is checked */}
            {formData.isEditDetailsExists &&
              formData.payment_details.paid_for_onboarding_kit && (
                <DatePicker
                  label="Subscription Start Date"
                  value={formData.payment_details.subscribe_start_date}
                  onChange={(date) =>
                    handleSubscriptionDateChange("subscribe_start_date", date)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      helperText="Start date of subscription"
                      required={
                        formData.payment_details.paid_for_onboarding_kit
                      }
                      error={
                        formData.payment_details.paid_for_onboarding_kit &&
                        !formData.payment_details.subscribe_start_date
                      }
                    />
                  )}
                />
              )}

            {/* Subscription End Date - Show only when Paid for Onboarding Kit is checked */}
            {formData.isEditDetailsExists &&
              formData.payment_details.paid_for_onboarding_kit && (
                <DatePicker
                  label="Subscription End Date"
                  value={formData.payment_details.subscribe_end_date}
                  onChange={(date) =>
                    handleSubscriptionDateChange("subscribe_end_date", date)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      helperText="End date of subscription"
                      required={
                        formData.payment_details.paid_for_onboarding_kit
                      }
                      error={
                        formData.payment_details.paid_for_onboarding_kit &&
                        !formData.payment_details.subscribe_end_date
                      }
                    />
                  )}
                />
              )}

            {/* Disable Login Switch */}
            {formData.isEditDetailsExists && (
              <FormControl fullWidth className="flex flex-row items-center">
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.login_into_application}
                        onChange={(e) =>
                          handleChangeCheckBox(e, "login_into_application")
                        }
                      />
                    }
                    label="Disable login"
                  />
                </FormGroup>
                <IconButton
                  onClick={() =>
                    handleWhatsAppClick(
                      formData.center_name,
                      formData.personal_details.phone.mobile_number
                    )
                  }
                  sx={{ width: "50px", height: "50px" }}
                >
                  <WhatsAppIcon sx={{ fontSize: "2rem", color: "green" }} />
                </IconButton>
              </FormControl>
            )}

            {/* Disable Accepting Ticket Switch */}
            {formData.isEditDetailsExists && (
              <FormControl fullWidth>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.accepting_broadcast_ticket}
                        onChange={(e) =>
                          handleChangeCheckBox(e, "accepting_broadcast_ticket")
                        }
                      />
                    }
                    label="Disable accepting ticket"
                  />
                </FormGroup>
              </FormControl>
            )}

            {/* Disable Remark */}
            {formData.remarkRequired && (
              <TextField
                fullWidth
                label="Disable Remark"
                variant="outlined"
                name="remark"
                value={formData.remark}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    remark: e.target.value,
                  }))
                }
              />
            )}

            {/* Pincode */}
            <TextField
              fullWidth
              helperText="Please enter your pincode"
              label="Area Pincode (main)"
              name="pincode"
              type="number"
              disabled={formData.isEditDetailsExists}
              onChange={handleChangeAddressDetails}
              error={formData.isFormValid && !formData.address_details.pincode}
              value={formData.address_details.pincode}
              inputProps={{ maxLength: 6 }}
            />

            {/* Service Area Pincodes */}
            <TextField
              fullWidth
              id="main-service-area-pincodes"
              label="Service Area Pincodes (secondary)"
              name="pincodes"
              type="text"
              onChange={handleChangePincodes}
              value={formData.pincodes}
              helperText="Please enter multiple pincodes separated by commas"
              inputProps={{ maxLength: 200 }}
            />

            {/* Technician Associate */}
            <FormControl fullWidth>
              <InputLabel id="technician-associate-label">
                Technician Associate
              </InputLabel>
              <Select
                labelId="technician-associate-label"
                id="technician-associate-select"
                label="Technician Associate"
                name="no_of_technicians"
                onChange={handleChangeInputOuter}
                error={
                  formData.isFormValid && formData.no_of_technicians === ""
                }
                value={formData.no_of_technicians}
              >
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((ite) => {
                  return <MenuItem value={ite}>{ite}</MenuItem>;
                })}
              </Select>
            </FormControl>

            {/* Automate QR Code */}
            {!formData.isEditDetailsExists && (
              <FormControl fullWidth>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.automate_qr_code}
                        onChange={(e) =>
                          handleChangeCheckBox1(e, "automate_qr_code")
                        }
                      />
                    }
                    label="Automate qr code"
                  />
                </FormGroup>
              </FormControl>
            )}

            {/* QR Code */}
            {!formData.isEditDetailsExists && !formData.automate_qr_code && (
              <TextField
                fullWidth
                helperText="Please enter your additional pincode"
                label="Enter qr code"
                name="qr_code"
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    qr_code: e.target.value,
                  }));
                }}
                value={formData.qr_code}
                inputProps={{ maxLength: 10 }}
              />
            )}

            {/* Email Address */}
            <TextField
              fullWidth
              helperText="Please provide your email address"
              label="Email Address"
              name="email"
              type="email"
              onChange={handleChangePersonalDetails}
              value={formData.personal_details.email}
              error={
                formData.personal_details.email &&
                !isValidEmail(formData.personal_details.email)
              }
              inputProps={{ maxLength: 100 }}
            />

            {/* UPI ID */}
            {formData.isEditDetailsExists && (
              <TextField
                fullWidth
                helperText="Your UPI Id"
                label="Enter upi id"
                name="upi_id"
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    payment_details: {
                      ...prev.payment_details,
                      upi_id: e.target.value,
                    },
                  }));
                }}
                value={formData.payment_details.upi_id}
                inputProps={{ maxLength: 20 }}
              />
            )}

            {/* QR ID */}
            <TextField
              fullWidth
              helperText="Your QR Id"
              label="Your QR Id"
              name="qr_id"
              disabled={true}
              value={formData.qr_id}
              inputProps={{ maxLength: 20 }}
            />

            {/* Center Remark */}
            {formData.isEditDetailsExists && (
              <TextField
                fullWidth
                label="Center Remark"
                name="ticket_remarks"
                value={formData.ticket_remarks}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    ticket_remarks: e.target.value,
                  }))
                }
              />
            )}

            {/* Submit Button */}
            <div className="col-span-1 md:col-span-2 flex justify-center mt-4">
              <Button
                onClick={handleRegister}
                disabled={formData.isRegistering}
                variant="contained"
                color="primary"
                size="large"
                className="w-full md:w-auto"
              >
                {formData.isRegistering
                  ? formData.isEditDetailsExists
                    ? "Updating...."
                    : "Adding ...."
                  : formData.isEditDetailsExists
                  ? "Update Center"
                  : "Add Center"}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </LocalizationProvider>
  );
};

export default CenterRegistration;
